import React from 'react'

import FileActionsSkeleton from 'modules/resultsV2/components/FileActionsSkeleton/FileActionsSkeleton'
import FileDetails from 'modules/resultsV2/components/FileDetails/FileDetails'
import * as styles from 'modules/resultsV2/components/ParticipantFileDetails/ParticipantFileDetails.styles'
import {
  DATA_ACCESS_REQUEST_PERSON_ID_FILE_TITLE,
  PERSON_ID_FILE_FAILED_MESSAGE,
  PERSON_ID_FILE_PREPARING_MESSAGE
} from 'modules/resultsV2/utils'
import { DataAccessRequestParticipantFileStatus } from 'services/results/enums'
import type { DataAccessRequestParticipantFile } from 'services/results/types'

import DownloadParticipantFile from './DownloadParticipantFile'

export const PARTICIPANT_FILE_ICON_TEST_ID = 'participant-file-icon'
export const PARTICIPANT_FILE_FAILED_ICON_TEST_ID = 'participant-file-failed-icon'

interface ParticipantFileDetailsProps {
  hasBorder?: boolean
  resultId: string
  participantFile?: DataAccessRequestParticipantFile
  /** Defaults to the export-request signed-URL download. Pass a DAR-specific action on the v3 details page. */
  downloadAction?: React.ReactNode
  isDownloadDisabled?: boolean
  isDownloadLoading?: boolean
}

const getReadyDownloadAction = ({
  downloadAction,
  isDownloadDisabled,
  isDownloadLoading,
  isReady,
  resultId
}: {
  downloadAction?: React.ReactNode
  isDownloadDisabled: boolean
  isDownloadLoading: boolean
  isReady: boolean
  resultId: string
}): React.ReactNode => {
  if (!isReady) return undefined
  if (isDownloadLoading) return <FileActionsSkeleton />
  if (isDownloadDisabled) return undefined

  return downloadAction ?? <DownloadParticipantFile resultId={resultId} />
}

const getSubtitle = (status: DataAccessRequestParticipantFileStatus, displayName?: string): React.ReactNode => {
  if (status === DataAccessRequestParticipantFileStatus.Preparing) return PERSON_ID_FILE_PREPARING_MESSAGE
  if (status === DataAccessRequestParticipantFileStatus.Failed) {
    return (
      <styles.SubtitleRow>
        <styles.ErrorIcon data-testid={PARTICIPANT_FILE_FAILED_ICON_TEST_ID} />
        <styles.SubtitleText>{PERSON_ID_FILE_FAILED_MESSAGE}</styles.SubtitleText>
      </styles.SubtitleRow>
    )
  }

  return displayName
}

/**
 * The person ID file is not part of the request's browsable location, so this row is driven entirely
 * by `participantFile` and downloads through its own signed-URL endpoint.
 */
const ParticipantFileDetails = ({
  hasBorder,
  resultId,
  participantFile,
  downloadAction,
  isDownloadDisabled = false,
  isDownloadLoading = false
}: ParticipantFileDetailsProps): JSX.Element | null => {
  if (!participantFile || participantFile.status === DataAccessRequestParticipantFileStatus.NotRequired) return null

  const { status, displayName } = participantFile
  const isReady = status === DataAccessRequestParticipantFileStatus.Ready
  const readyDownloadAction = getReadyDownloadAction({
    downloadAction,
    isDownloadDisabled,
    isDownloadLoading,
    isReady,
    resultId
  })

  return (
    <FileDetails
      displayName={DATA_ACCESS_REQUEST_PERSON_ID_FILE_TITLE}
      downloadAction={isReady ? readyDownloadAction : undefined}
      hasBorder={hasBorder}
      iconTestId={PARTICIPANT_FILE_ICON_TEST_ID}
      isFile
      subtitle={getSubtitle(status, displayName)}
      subtitleTitle={
        status === DataAccessRequestParticipantFileStatus.Failed ? PERSON_ID_FILE_FAILED_MESSAGE : undefined
      }
    />
  )
}

export default ParticipantFileDetails
